import React, { useState } from "react";
import { CalendarPlus, ArrowUpRight, Download } from "lucide-react";
import { toast } from "sonner";
import { PROTON_AFFILIATE_URL } from "../lib/proton";

const esc = (s) => String(s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
const icsTime = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

const localTime = (d) => { const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}T${p(d.getHours())}${p(d.getMinutes())}00`; };
const GENERIC_TITLE = "Sovereign Signal gathering";

// Built on-device from decrypted fields, so the server never sees the title or link.
// Proton (encrypted) gets full details; other calendars get only the time and a placeholder title.
const buildIcs = ({ id, uid, title, startsAt, link, details, hearthUrl, durationMin, rrule }, full) => {
  const start = new Date(startsAt);
  const end = new Date(start.getTime() + (durationMin || 60) * 60e3);
  const name = full ? title : GENERIC_TITLE;
  const desc = full ? [details, link && `Join: ${link}`, hearthUrl && `Hearth: ${hearthUrl}`].filter(Boolean).join("\n\n")
    : hearthUrl ? `Open the Hearth for the details and the link: ${hearthUrl}` : "Open Sovereign Signal for the details.";
  // Recurring series use the member's local time zone so the time holds through daylight saving changes.
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const when = rrule && tz ? [`DTSTART;TZID=${tz}:${localTime(start)}`, `DTEND;TZID=${tz}:${localTime(end)}`, `RRULE:${rrule}`]
    : [`DTSTART:${icsTime(start)}`, `DTEND:${icsTime(end)}`];
  return [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Sovereign Signal//Live Session//EN", "CALSCALE:GREGORIAN", "METHOD:PUBLISH",
    "BEGIN:VEVENT", `UID:${uid || id}@sovereignsignal`, `DTSTAMP:${icsTime(new Date())}`, ...when,
    `SUMMARY:${esc(name)}`, `DESCRIPTION:${esc(desc)}`, full && link ? `LOCATION:${esc(link)}` : null, full && link ? `URL:${link}` : null,
    "BEGIN:VALARM", "TRIGGER:-PT15M", "ACTION:DISPLAY", `DESCRIPTION:${esc(name)}`, "END:VALARM",
    "END:VEVENT", "END:VCALENDAR",
  ].filter(Boolean).join("\r\n");
};

const fileFor = (ev, full) => new File([buildIcs(ev, full)], `${((full && ev.title) || "sovereign-signal-gathering").replace(/[^\w-]+/g, "-").slice(0, 40)}.ics`, { type: "text/calendar" });

const download = (ev, full) => {
  const f = fileFor(ev, full);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(f);
  a.download = f.name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
};

// Proton can't receive events from other apps, so phones get the Share sheet (Save to Files), then import in Proton.
const saveForProton = async (ev) => {
  const f = fileFor(ev, true);
  if (navigator.canShare && navigator.canShare({ files: [f] })) {
    try { await navigator.share({ files: [f], title: ev.title }); } catch (e) { if (e?.name === "AbortError") return false; download(ev, true); }
  } else download(ev, true);
  return true;
};

const ProtonSteps = () => (
  <div className="border border-[#6D4AFF]/30 bg-[#6D4AFF]/[0.06] p-3 font-body text-xs text-zinc-300 space-y-1.5" data-testid="calendar-proton-steps">
    <p className="text-white font-medium">Two steps to add it to Proton Calendar:</p>
    <p>1. Save the file. On iPhone, choose <span className="text-white">Save to Files</span>.</p>
    <p>2. Open Proton Calendar on the web, go to <span className="text-white">Settings › Import</span> and choose the file.</p>
    <a href="https://calendar.proton.me" target="_blank" rel="noopener noreferrer" data-testid="calendar-proton-open" className="inline-flex items-center gap-1 text-[#8B6DFF] hover:underline">Open Proton Calendar <ArrowUpRight size={12} /></a>
  </div>
);

export const AddToCalendar = ({ ev, label = "Add to calendar" }) => {
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState(false);
  const proton = async () => { if (await saveForProton(ev)) setSteps(true); };
  const other = () => { download(ev, false); toast.success("Saved. Open the file to add it to your calendar."); };
  return (
    <div className="mt-2" data-testid={`add-to-calendar-${ev.id}`}>
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`add-to-calendar-btn-${ev.id}`}
        className="inline-flex items-center gap-1.5 font-body text-xs text-zinc-400 hover:text-white transition-colors"><CalendarPlus size={13} /> {label}</button>
      {open && (
        <div className="mt-2 border border-white/10 bg-[#0A0A0A] p-3 space-y-2 fade-up">
          <button onClick={proton} data-testid={`calendar-proton-${ev.id}`} className="w-full flex items-center justify-between gap-2 border border-[#6D4AFF]/50 text-white px-3 py-2 font-body text-sm hover:bg-[#6D4AFF]/10 transition-colors">
            <span>Proton Calendar <span className="text-zinc-500 text-xs">· private & encrypted</span></span><Download size={14} className="text-[#8B6DFF]" />
          </button>
          {steps && <ProtonSteps />}
          <button onClick={other} data-testid={`calendar-other-${ev.id}`} className="w-full flex items-center justify-between gap-2 border border-white/10 text-zinc-300 px-3 py-2 font-body text-sm hover:bg-white/5 transition-colors">
            <span>Apple, Google or Outlook <span className="text-zinc-500 text-xs">· time only</span></span><Download size={14} className="text-zinc-500" />
          </button>
          <a href={PROTON_AFFILIATE_URL} target="_blank" rel="noopener noreferrer" data-testid={`calendar-proton-signup-${ev.id}`}
            className="flex items-center gap-1 font-body text-xs text-[#8B6DFF] hover:underline pt-1">Need a Proton account? Start with free Proton Mail <ArrowUpRight size={12} /></a>
          <p className="font-body text-[11px] text-zinc-600">Proton Calendar is encrypted, so it gets the full details and the Meet link. Apple, Google and Outlook get only the time and a private placeholder title; the link stays here. Both include a 15 minute reminder, and the file is made on your device, never on our servers.</p>
        </div>
      )}
    </div>
  );
};
